import window from '../window/index.js';
import Emitter from '../common/Emitter.js';
import { Store } from '../hali/index.js';
import {
  SCROLL_PERC_PRECISION,
  SCROLL_TIME_FLOAT_PRECISION,
} from '../constants.js';
import chooseBreakpoint from './breakpoints.js';
import {
  mix,
  once,
  clamp,
  sortBy,
  roundTo,
  uniqueId,
} from '@immutabl3/utils';
import {
  scale as calculateScale,
  height as calculateHeight,
  scrollPerc as calculateScrollPerc,
} from './calculate.js';

export default function Beaker({
  key = '',
  id = uniqueId(),
  breakpoints = [],
  precision = SCROLL_TIME_FLOAT_PRECISION,
} = {}) {
  const configs = sortBy(breakpoints, ({ minWidth = 0 }) => minWidth);
  
  const store = Store({
    key,

    visible: false,
    
    ticks: 0,
    scrollTop: 0,
    top: 0,
    height: 0,
    perc: 0,
    time: 0,
    scale: 1,
    duration: 0,
    padding: 0,
    fontSize: 0,

    viewportWidth: 0,
    viewportHeight: 0,
  });

  let config = {};
  let isLast = false;

  const applyBreakpoint = obj => {
    config = obj;
    
    store.scale = calculateScale(config.width, config.height);
    store.height = calculateHeight(
      config.duration,
      config.scrollScale,
    );

    store.duration = config.duration;
    store.padding = config.padding;
    store.fontSize = config.fontSize;
  };

  return mix(Emitter(), {
    get isBeaker() { return true; },

    get id() { return id; },
    get key() { return key; },
    
    get store() { return store; },
    
    get ready() { return store.ready; },
    get visible() { return store.visible; },
    get duration() { return store.duration; },

    set last(bool) { isLast = bool; },

    // the top of the beaker is determined
    // by the top of the lab and all beakers
    // that came before it. we don't calculate
    // it here. it is needed on scroll to determine
    // the scroll percentage 
    get top() { return store.top; },
    set top(scrollTop) { store.top = scrollTop; },

    // expose the current config to allow for
    // lab to manipulate positioning
    get config() { return config; },

    // height is used by lab to determine
    // beaker positions, but is also needed
    // here to calculate scroll percentage.
    // it's also utilized in useAnchor
    get height() { return store.height; },

    init: once(() => {
      store.viewportWidth = window.width();
      store.viewportHeight = window.height();

      applyBreakpoint(chooseBreakpoint(configs, store.viewportWidth));
    }),

    scroll(scrollTop) {
      store.ticks += 1;
      
      const sp = calculateScrollPerc(scrollTop, store.top, store.height, isLast);
      const scrollPerc = roundTo(sp, SCROLL_PERC_PRECISION);

      store.visible = scrollPerc >= 0 && scrollPerc <= 1;

      const perc = clamp(scrollPerc, 0, 1);
      const time = roundTo(config.duration * perc, precision);

      store.perc = perc;
      store.time = time;
      store.scrollTop = scrollTop;
    },

    resize() {
      store.viewportWidth = window.width();
      store.viewportHeight = window.height();

      const newConfig = chooseBreakpoint(configs, store.viewportWidth);
      const isDirty = config.minWidth !== newConfig.minWidth;
      
      if (isDirty) applyBreakpoint(newConfig);
    },
  });
};